/**
 * Utility functions for user data handling
 */

export interface UserData {
  id?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  profilePic?: string | null;
  role?: string;
  phone?: string;
  location?: string;
  birthday?: string;
  bio?: string;
  joinDate?: string;
  firstimeSetup?: number;
}

/**
 * Generate user initials from user data
 * Priority: firstName + lastName > name > email > 'U'
 */
export const getUserInitials = (user: UserData): string => {
  // Try firstName + lastName first
  if (user.firstName && user.lastName) {
    return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
  }
  
  // Try firstName only
  if (user.firstName) {
    return user.firstName.charAt(0).toUpperCase();
  }
  
  // Try name field (split by space)
  if (user.name) {
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
    }
    return nameParts[0].charAt(0).toUpperCase();
  }
  
  // Try email
  if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }
  
  // Default fallback
  return 'U';
};

/**
 * Get user display name
 * Priority: firstName + lastName > name > email
 */
export const getUserDisplayName = (user: UserData): string => {
  // Try firstName + lastName
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  
  // Try firstName only
  if (user.firstName) {
    return user.firstName;
  }
  
  // Try name field
  if (user.name) {
    return user.name;
  }
  
  // Try email
  if (user.email) {
    return user.email;
  }
  
  return 'User';
};

/**
 * Check if user has profile picture
 */
export const hasProfilePicture = (user: UserData): boolean => {
  return !!(user.profilePic && user.profilePic.trim() !== '');
};

/**
 * Get profile picture URL or null
 */
export const getProfilePictureUrl = (user: UserData): string | null => {
  if (hasProfilePicture(user)) {
    return user.profilePic!;
  }
  return null;
};

/**
 * Merge user data (useful for updating user context)
 */
export const mergeUserData = (currentUser: UserData, updates: Partial<UserData>): UserData => {
  return {
    ...currentUser,
    ...updates
  };
};

/**
 * Format user data for display
 */
export const formatUserForDisplay = (user: UserData) => {
  return {
    initials: getUserInitials(user),
    displayName: getUserDisplayName(user),
    profilePicture: getProfilePictureUrl(user),
    hasProfilePic: hasProfilePicture(user)
  };
};
